import { Link } from 'react-router-dom';
import { Award, Lock, ShieldCheck, Megaphone, Globe, Camera, MapPin, FileText, Calculator, Briefcase, ArrowRight, Send } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const motiviSection = [
  {
    icon: Award,
    num: '01',
    title: 'Professionalità',
    short: 'Agenti qualificati con abilitazione professionale e formazione continua.',
    long: "Vendere il proprio immobile significa affidare nelle mani di un professionista il bene di maggiore valore di cui si è in possesso: la casa. L'Agente Immobiliare Green House ha seguito un percorso di studi che l'ha portato al conseguimento dell'abilitazione alla professione, ha ottemperato a tutti gli adempimenti burocratici necessari (assicurazione professionale, SCIA camera di commercio, partita IVA) per essere considerato un professionista a tutti gli effetti.",
  },
  {
    icon: Lock,
    num: '02',
    title: 'Esclusività',
    short: "L'incarico in esclusiva: un valore aggiunto per la vendita del tuo immobile.",
    long: "Un prodotto \"esclusivo\" è un prodotto riservato ad un numero ristretto e qualificato di persone. L'Agente Immobiliare, detenendo l'autorizzazione alla vendita in Esclusiva, proporrà l'immobile al massimo delle proprie potenzialità, con la certezza che i potenziali acquirenti non troveranno lo stesso annuncio da più agenzie. Questo elimina le domande dei clienti come: \"Come mai servono 10 agenzie per vendere questo immobile? C'è forse qualche problema?\"",
  },
  {
    icon: ShieldCheck,
    num: '03',
    title: 'Qualità',
    short: 'Screening documentale approfondito per la massima serenità di entrambe le parti.',
    long: "L'Agente Immobiliare effettua uno screening approfondito della documentazione ipotecaria, notarile, urbanistica e catastale dell'immobile, avvalendosi della collaborazione decennale con professionisti del settore. Il venditore viene informato sin da subito circa gli eventuali adeguamenti necessari, evitando brutte sorprese a ridosso del rogito. L'acquirente riceve tutta la documentazione necessaria al vaglio della Banca e del Notaio.",
  },
  {
    icon: Megaphone,
    num: '04',
    title: 'Pubblicità',
    short: 'Massima visibilità su tutti i principali portali immobiliari e canali offline.',
    long: "La Green House è leader nel settore per investimenti pubblicitari, essendo partner di tutte le principali piattaforme: Immobiliare.it, Casa.it, Idealista, WikiCasa, Attico.it e Subito. Ogni immobile è pubblicizzato con extra visibilità e sponsorizzate a pagamento. Inoltre, realizziamo 10.000 copie mensili di volantini distribuiti sul territorio e pannelli retroilluminati nelle nostre vetrine su Via Caduti sul Lavoro.",
  },
  {
    icon: Globe,
    num: '05',
    title: 'Presenza Digitale',
    short: 'Sito web all\'avanguardia, social media e strumenti innovativi.',
    long: "Su www.greenhousecaserta.it il cliente trova una piattaforma moderna con servizi non presenti sui portali: APE, Servizi Catastali, Urbanistici e Notarili, Consulenze di Mutuo. Gli annunci includono book fotografici con oltre 50 scatti in alta qualità, planimetrie, video e il servizio di Virtual Tour 360°. L'83% dei feedback deriva dalla pubblicità online, e i nostri post giornalieri su Facebook e Instagram amplificano la visibilità.",
  },
];

const serviziTecnici = [
  {
    icon: Camera,
    title: 'Virtual Tour 360°',
    desc: "Connettendo i clienti all'immobile da qualsiasi parte del mondo, il Virtual Tour permette una visita virtuale in ogni momento, da condividere con parenti, amici, l'architetto e la ditta appaltatrice.",
    requestable: false,
  },
  {
    icon: FileText,
    title: 'A.P.E. - Attestato Prestazione Energetica',
    desc: "Redazione dell'Attestato di Prestazione Energetica obbligatorio per legge in fase di compravendita e locazione, con tecnici abilitati.",
    requestable: true,
  },
  {
    icon: MapPin,
    title: 'Servizi Catastali e Urbanistici',
    desc: "Verifica e aggiornamento della documentazione catastale e urbanistica dell'immobile, incluse visure, planimetrie e pratiche di conformità.",
    requestable: true,
  },
  {
    icon: Briefcase,
    title: 'Assistenza Notarile',
    desc: "Collaborazione con studi notarili per la gestione di tutti gli adempimenti legali, dalla proposta d'acquisto fino al rogito definitivo.",
    requestable: false,
  },
  {
    icon: Calculator,
    title: 'Consulenza Mutuo',
    desc: "Supporto nella ricerca delle migliori condizioni di finanziamento, con consulenza dedicata per ottenere il mutuo più adatto alle tue esigenze.",
    requestable: true,
  },
];

export default function ServicesPage() {
  const [expandedMotivo, setExpandedMotivo] = useState(null);
  const [serviceModal, setServiceModal] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: '', email: '', phone: '', message: '', privacy: false });
  const [submitting, setSubmitting] = useState(false);

  const handleServiceRequest = async (e) => {
    e.preventDefault();
    if (!serviceForm.privacy) {
      toast.error('Devi accettare la Privacy Policy per continuare');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/service-requests`, {
        name: serviceForm.name,
        email: serviceForm.email,
        phone: serviceForm.phone,
        message: serviceForm.message,
        service_type: serviceModal,
      });
      toast.success('Richiesta inviata con successo!');
      setServiceForm({ name: '', email: '', phone: '', message: '', privacy: false });
      setServiceModal(null);
    } catch {
      toast.error("Errore nell'invio della richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fadeIn">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-accent mb-4">
              I Nostri Servizi
            </p>
            <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-6">
              5 motivi per vendere con Green House
            </h1>
            <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed">
              Dal 2011 offriamo servizi immobiliari completi a Caserta e provincia.
              Professionalità, esclusività e innovazione al servizio del tuo immobile.
            </p>
          </div>
        </div>
      </section>

      {/* 5 Motivi */}
      <section className="py-24 bg-background" data-testid="motivi-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
              Perché Sceglierci
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">
              I 5 pilastri del nostro servizio
            </h2>
          </div>

          <div className="space-y-6">
            {motiviSection.map((motivo, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden transition-shadow hover:shadow-lg animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <button
                  onClick={() => setExpandedMotivo(expandedMotivo === index ? null : index)}
                  className="w-full flex items-center gap-6 p-6 md:p-8 text-left"
                  data-testid={`motivo-${index}`}
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <motivo.icon size={28} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-sans text-xs font-bold text-primary tracking-widest">{motivo.num}</span>
                      <h3 className="font-serif text-xl md:text-2xl text-text-main">{motivo.title}</h3>
                    </div>
                    <p className="font-sans text-sm text-text-muted">{motivo.short}</p>
                  </div>
                  <div className={`text-primary transition-transform flex-shrink-0 ${expandedMotivo === index ? 'rotate-180' : ''}`}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </button>
                {expandedMotivo === index && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 ml-20">
                    <p className="font-sans text-text-muted leading-relaxed">{motivo.long}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Servizi Tecnici */}
      <section className="py-24 bg-background-alt" data-testid="servizi-tecnici-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
              Servizi Dedicati
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">
              I nostri servizi tecnici
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviziTecnici.map((servizio, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl hover:shadow-xl transition-shadow animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <servizio.icon size={28} className="text-primary" />
                </div>
                <h3 className="font-serif text-xl text-text-main mb-3">{servizio.title}</h3>
                <p className="font-sans text-sm text-text-muted leading-relaxed mb-4">{servizio.desc}</p>
                {servizio.requestable && (
                  <Button
                    onClick={() => setServiceModal(servizio.title)}
                    className="w-full bg-primary hover:bg-primary-hover text-white"
                    data-testid={`request-${index}`}
                  >
                    <Send size={16} className="mr-2" />
                    Richiedi
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Service Request Modal */}
          <Dialog open={!!serviceModal} onOpenChange={(o) => !o && setServiceModal(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Richiesta: {serviceModal}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleServiceRequest} className="space-y-4" data-testid="service-request-form">
                <Input placeholder="Nome e Cognome *" value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} required data-testid="service-name" />
                <Input type="email" placeholder="Email *" value={serviceForm.email} onChange={(e) => setServiceForm({ ...serviceForm, email: e.target.value })} required data-testid="service-email" />
                <Input type="tel" placeholder="Telefono *" value={serviceForm.phone} onChange={(e) => setServiceForm({ ...serviceForm, phone: e.target.value })} required data-testid="service-phone" />
                <Textarea placeholder="Dettagli della richiesta (facoltativo)" value={serviceForm.message} onChange={(e) => setServiceForm({ ...serviceForm, message: e.target.value })} rows={3} data-testid="service-message" />
                <label className="flex items-start gap-2 text-xs font-sans text-text-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={serviceForm.privacy}
                    onChange={(e) => setServiceForm({ ...serviceForm, privacy: e.target.checked })}
                    required
                    className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                    data-testid="service-privacy-checkbox"
                  />
                  <span>
                    Dichiaro di aver letto e accetto la{' '}
                    <a href="https://www.iubenda.com/privacy-policy/84083459" className="text-primary underline hover:text-primary-hover" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    {' '}(GDPR) *
                  </span>
                </label>
                <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" disabled={submitting} data-testid="service-submit">
                  {submitting ? 'Invio...' : 'Invia Richiesta'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-background" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
              Come Lavoriamo
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-main tracking-tight">
              Il nostro processo
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Contatto', desc: 'Ci conosci e ci racconti le tue esigenze' },
              { step: '02', title: 'Valutazione', desc: 'Screening documentale e stima professionale' },
              { step: '03', title: 'Promozione', desc: 'Pubblicità su tutti i canali con massima visibilità' },
              { step: '04', title: 'Vendita', desc: 'Ti accompagniamo fino al rogito notarile' },
            ].map((item, index) => (
              <div key={index} className="text-center animate-fadeIn" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-serif text-xl font-semibold">{item.step}</span>
                </div>
                <h3 className="font-serif text-xl text-text-main mb-2">{item.title}</h3>
                <p className="font-sans text-sm text-text-muted">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portali Partner */}
      <section className="py-16 bg-background-alt" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-primary mb-4">
              I Nostri Partner
            </p>
            <h2 className="font-serif text-3xl text-text-main tracking-tight">
              Presenti su tutti i portali
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {['Immobiliare.it', 'Casa.it', 'Idealista', 'WikiCasa', 'Attico.it', 'Subito.it'].map((portal) => (
              <span key={portal} className="font-sans text-lg md:text-xl text-text-muted font-medium">{portal}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-6">
            Hai bisogno di assistenza?
          </h2>
          <p className="font-sans text-base md:text-lg text-white/80 mb-8">
            Contattaci per una consulenza gratuita e senza impegno.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-md font-sans font-medium transition-all"
            data-testid="services-contact-cta"
          >
            Contattaci
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
